import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { FC } from 'react'
import { PostLoginErrorKind } from './post-login-load'

interface Props {
  variant: PostLoginErrorKind
  onRetry: () => void
  onLogout: () => void
}

/**
 * Full-screen fallback shown when the post-login load fails. Self-contained on
 * purpose: it renders BEFORE the app shell (RootElement / SnackbarToast) mounts,
 * so it cannot rely on the global toast or layout.
 */
export const PostLoginErrorScreen: FC<Props> = ({ variant, onRetry, onLogout }) => {
  const { t } = useTranslate()

  const message =
    variant === 'no-tenant'
      ? t(
          'postLoginErrorNoTenant',
          'Your account is not assigned to a tenant yet. Please contact your administrator.',
        )
      : t(
          'postLoginErrorGeneric',
          'Something went wrong while loading your workspace. Please try again.',
        )

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Stack spacing={3} alignItems='center' textAlign='center' maxWidth={420}>
        <Typography variant='h5'>
          {t('postLoginErrorTitle', "We couldn't load your workspace")}
        </Typography>

        <Typography variant='body1' color='text.secondary'>
          {message}
        </Typography>

        <Stack direction='row' spacing={2}>
          {variant !== 'no-tenant' && (
            <Button variant='contained' onClick={onRetry}>
              {t('tryAgain', 'Try again')}
            </Button>
          )}
          <Button variant='outlined' onClick={onLogout}>
            {t('logout', 'Logout')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
